'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { HiFilter } from 'react-icons/hi';

import styles from './FilterDropdown.module.css';

export type FilterSection = {
  title: string;
  type: 'checkbox' | 'radio';
  key: string;
  options: Array<{ value: string; label: string }>;
};

type Props = {
  label?: string;
  sections: Array<FilterSection>;
  values: Record<string, Array<string>>;
  onApply: (values: Record<string, Array<string>>) => void;
  onReset: () => void;
};

const FilterDropdown: React.FC<Props> = ({
  label = 'Filter',
  sections,
  values,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localValues, setLocalValues] = useState<Record<string, Array<string>>>(values);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const activeCount = Object.values(values).reduce((sum, arr) => sum + arr.length, 0);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) setLocalValues(values);
      return !prev;
    });
  }, [values]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleCheckboxChange = useCallback((sectionKey: string, optionValue: string) => {
    setLocalValues(prev => {
      const current = prev[sectionKey] || [];
      const exists = current.includes(optionValue);
      return {
        ...prev,
        [sectionKey]: exists
          ? current.filter(v => v !== optionValue)
          : [...current, optionValue],
      };
    });
  }, []);

  const handleRadioChange = useCallback((sectionKey: string, optionValue: string) => {
    setLocalValues(prev => ({
      ...prev,
      [sectionKey]: optionValue ? [optionValue] : [],
    }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(localValues);
    setIsOpen(false);
  }, [localValues, onApply]);

  const handleReset = useCallback(() => {
    onReset();
    setIsOpen(false);
  }, [onReset]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button className={styles.trigger} onClick={handleToggle}>
        <HiFilter size={16} />
        {label}
        {activeCount > 0 && <span className={styles.badge}>{activeCount}</span>}
      </button>

      {isOpen && (
        <>
          <div className={styles.overlay} onClick={handleClose} />
          <div className={styles.dropdown}>
            <div className={styles.dropdownBody}>
              {sections.map(section => (
                <div key={section.key}>
                  <div className={styles.sectionTitle}>{section.title}</div>
                  {section.type === 'checkbox' ? (
                    <div className={styles.checkboxList}>
                      {section.options.map(opt => (
                        <label key={opt.value} className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={(localValues[section.key] || []).includes(opt.value)}
                            onChange={() => handleCheckboxChange(section.key, opt.value)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.radioList}>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name={section.key}
                          checked={(localValues[section.key] || []).length === 0}
                          onChange={() => handleRadioChange(section.key, '')}
                        />
                        All
                      </label>
                      {section.options.map(opt => (
                        <label key={opt.value} className={styles.radioLabel}>
                          <input
                            type="radio"
                            name={section.key}
                            checked={(localValues[section.key] || []).includes(opt.value)}
                            onChange={() => handleRadioChange(section.key, opt.value)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className={styles.dropdownFooter}>
              <button className={styles.resetButton} onClick={handleReset}>
                Reset
              </button>
              <button className={styles.applyButton} onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FilterDropdown;
